import { useId } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Select from "@/components/ui/Select";
import { ButtonShadcn } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * @param {number[]} [pageSizeOptions]  the sizes this list offers, when its
 *   rows are heavier or lighter than the default three suit.
 * @param {string} [summary]  replaces "Showing X-Y of N" for a list whose page
 *   holds a different number of rows than it holds items — the message log
 *   pages by sent message, and a page renders one extra row per reply, so an
 *   exact row range would lie.
 */
const ListPagination = ({ page, pageSize, total, onPageChange, onPageSizeChange, label = "items", pageSizeOptions = PAGE_SIZE_OPTIONS, summary, className = "" }) => {
  const pageSizeId = useId();
  const pageSizeLabelId = `${pageSizeId}-label`;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className={cn("flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border-primary bg-background-primary/40 px-1 py-2", className)}>
      <div className="flex items-center gap-2">
        <label id={pageSizeLabelId} htmlFor={pageSizeId} className="text-xs text-text-secondary">
          Items per page
        </label>
        <div className="w-20 shrink-0">
          <Select
            id={pageSizeId}
            aria-labelledby={pageSizeLabelId}
            value={pageSize}
            onChange={(value) => onPageSizeChange(Number(value))}
            options={pageSizeOptions.map((value) => ({ value, label: String(value) }))}
            className="h-7 rounded-lg px-2 py-1 text-xs font-normal"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs text-text-secondary">{summary ?? (total === 0 ? `No ${label}` : `Showing ${start}-${end} of ${total} ${label}`)}</span>
        <div className="flex items-center gap-1">
          <ButtonShadcn
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            aria-label="Previous page"
            className="text-text-secondary hover:text-text-primary"
          >
            <ChevronLeft size={14} />
          </ButtonShadcn>
          <ButtonShadcn
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            aria-label="Next page"
            className="text-text-secondary hover:text-text-primary"
          >
            <ChevronRight size={14} />
          </ButtonShadcn>
        </div>
      </div>
    </div>
  );
};

export default ListPagination;
